import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, PageHeader, Pill } from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { useGetAudienceCampaignReports } from '../../../../../api/dealership/campaigns/reports/useGetAudienceCampaignReports'
import { useParams } from 'react-router'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import { useGetAudienceCampaignById } from '../../../../../api/dealership/campaigns/useGetAudienceCampaignById'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { DateRange } from '../../../../../lib/types/DateRange'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'

const AudienceCampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()
  const { data: campaign } = useGetAudienceCampaignById({ campaign_id })
  const { data: { overallMetrics = { impressions: 0, clicks: 0 } } = {} } =
    useGetAudienceCampaignReports({
      campaign_id: campaign_id,
    })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const { mutateAsync: downloadReport, isLoading: isLoadingReport } =
    useDownloadPDFReport()

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={'../'}
        title={`${dealership?.dealership} - ${campaign?.vendor_campaign_name ?? ''}`}
        cardsOptions={[
          {
            title: 'Total Clicks',
            content: formatNumber(overallMetrics?.clicks || 0),
            color: 'bg-chrysler-blue/20',
          },
          {
            title: 'Total Impressions',
            content: formatNumber(overallMetrics?.impressions || 0),
            color: 'bg-blue-aero/20',
          },
          {
            title: 'CTR',
            content: `${Math.round(
              (overallMetrics?.clicks / (overallMetrics?.impressions || 1)) * 100,
            )}%`,
            color: 'bg-secondary-malachite-green/30',
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isLoadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'audience',
                    campaign_id: campaign?.client_campaign_id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'audience', campaign?.client_campaign_id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.vendor_campaign_name}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-10'} />
        {children}
      </div>
    </div>
  )
}

const AudienceCampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange } = useDateRange()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const {
    data: { metrics, audience: { trends: audienceTrends, size: audienceSize } } = {
      metrics: [],
      audience: { trends: [], size: 0 },
    },
    isLoading: isLoadingAudienceReports,
    isPreviousData: isPreviousAudienceReportsData,
  } = useGetAudienceCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  useEffect(() => {
    if (!isLoadingAudienceReports) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingAudienceReports])

  const audienceIncreasePercentage: {
    value: string
    type: 'success' | 'error' | 'warning'
  } = useMemo(() => {
    if (audienceTrends?.length > 0) {
      const last = audienceTrends[audienceTrends.length - 1]
      const first = audienceTrends[0]
      const trend = last.customersSize - first.customersSize || 0
      if (trend > 0) {
        return {
          value: `+${((trend / first.customersSize) * 100).toFixed(2)}%`,
          type: 'success',
        }
      } else if (trend < 0) {
        return {
          value: `${((trend / first.customersSize) * 100).toFixed(2)}%`,
          type: 'error',
        }
      }
      return {
        value: `0%`,
        type: 'warning',
      }
    }
    return {
      value: '0%',
      type: 'success',
    }
  }, [audienceTrends])

  return (
    <AudienceCampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'flex flex-col gap-8'}>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Clicks</span>
                <span>
                  {new Intl.NumberFormat('en-US').format(
                    metrics
                      ?.map(
                        (metric: { impressions: number; clicks: number }) =>
                          metric.clicks,
                      )
                      .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'blue-to-purple'}
                  yKey={'clicks'}
                  isLoading={isLoadingAudienceReports}
                  isPreviousData={isPreviousAudienceReportsData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={`pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic ${
                  !metrics && 'opacity-50'
                }`}>
                <span>Total Impressions</span>
                <span>
                  {new Intl.NumberFormat('en-US').format(
                    metrics
                      ?.map((metric: { impressions: number }) => metric.impressions)
                      .reduce((acc: number, cur: number) => acc + cur, 0),
                  )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'red-to-orange'}
                  yKey={'impressions'}
                  isLoading={isLoadingAudienceReports}
                  isPreviousData={isPreviousAudienceReportsData}
                />
              </div>
            </div>
          </div>
          <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Audience size trend</span>
              <div className={'flex items-center gap-2'}>
                {new Intl.NumberFormat('en-US').format(audienceSize)}
                <Pill type={audienceIncreasePercentage?.type}>
                  {audienceIncreasePercentage?.value}
                </Pill>
              </div>
            </div>
            <div className={'w-full h-[320px] p-2'}>
              <LineChart
                data={[]}
                gradient={'green-to-lemon-green'}
                yKey={'customersSize'}
                isLoading={isLoadingAudienceReports}
                isPreviousData={isPreviousAudienceReportsData}
                borderColor={'#0A9652'}
              />
            </div>
          </div>
        </div>
      </div>
    </AudienceCampaignTemplate>
  )
}

export default AudienceCampaign
